import { msg, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import { useEffect, useRef, useState } from "react";
import ContainerTextHeader from "../common/ContainerTextHeader";
import Footer from "../common/Footer";
import FooterButton from "../common/FooterButton";
import { PopUpMessageView } from "../common/PopUpMessageView";
import useDisplayMessage from "../common/lib/hooks/useDisplayMessage";
import useInterval from "../common/lib/hooks/useInterval";
import useIsAnonymous from "../common/lib/hooks/useIsAnonymous";
import useTicketReprint from "../common/lib/hooks/useTicketReprint";
import SwitchToMobileQRCode from "../common/SwitchToMobileQRCode";
import ReprintAcceptButton from "../dev/ReprintAcceptButton";
import ReprintFailButton from "../dev/ReprintFailButton";
import ScanButton from "../dev/ScanButton";
import { useGetPastOrderByStagedOrderId } from "../requests/useGetPastOrderByStagedOrderId";

interface TicketInstructionsProps {
  stagedOrderId: string,
  forLoadoutGroup: boolean,
  additionalInstructions: string | undefined,
  loadCompleted: () => void,
}

const TicketInstructions = (props: TicketInstructionsProps) => {
  const { _ } = useLingui();

  const [stagedOrderComplete, setStagedOrderComplete] = useState<boolean>(false);
  const [reprintWindowOpen, setReprintWindowOpen] = useState<boolean>(true);
  const reprintWindowStart = useRef<number>(0);
  const isAnonymous = useIsAnonymous();
  const displayMessage = useDisplayMessage();

  const refreshTimeMs = 3000;
  const completeTimeMs = 15000;
  const reprintWindowMs = 600000;

  const { state: reprintState, isRequesting, requestReprint } = useTicketReprint(props.stagedOrderId);

  const stageOrderCompleted = useGetPastOrderByStagedOrderId(props.stagedOrderId, {
    onSuccess: () => {
      if (reprintWindowStart.current === 0) reprintWindowStart.current = Date.now();
      setStagedOrderComplete(true);
    }
  });

  useInterval(() => {
    stageOrderCompleted.query();

    if (reprintWindowStart.current !== 0 && Date.now() - reprintWindowStart.current >= reprintWindowMs)
      setReprintWindowOpen(false);
  }, refreshTimeMs, true);

  useInterval(() => isAnonymous && stagedOrderComplete ? props.loadCompleted() : null, completeTimeMs, false);

  useEffect(() => {
    switch (reprintState.status) {
      case 'succeeded':
        displayMessage.success(_(msg`Your ticket is reprinting. Please collect it at the printer.`));
        break;
      case 'failed':
        displayMessage.fail(_(reprintState.message));
        break;
    }
  }, [reprintState.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const onReprintClick = () => {
    displayMessage.clear();
    requestReprint();
  };

  const reprintDisabled = !stagedOrderComplete
    || isRequesting
    || reprintState.status === 'pending'
    || reprintState.status === 'succeeded'
    || (reprintState.status === 'failed' && !reprintState.retryable);

  const reprintButton = !isAnonymous && reprintWindowOpen
    ? <FooterButton
      disabled={reprintDisabled}
      spinning={isRequesting || reprintState.status === 'pending'}
      onClick={onReprintClick}>
      <Trans>Reprint ticket</Trans>
    </FooterButton>
    : undefined;

  return <PopUpMessageView
    message={displayMessage.message?.message}
    messageAcknowledged={displayMessage.clear}>
    <ContainerTextHeader
      message={props.forLoadoutGroup ? _(msg`Pick up ticket(s) at printer`) : _(msg`Pick up ticket at printer`)}
      devToolItems={<>
        <ScanButton stagedOrderId={props.stagedOrderId} location='TicketCreated' />
        <ReprintAcceptButton stagedOrderId={props.stagedOrderId} />
        <ReprintFailButton stagedOrderId={props.stagedOrderId} />
      </>}>
      {isAnonymous && <SwitchToMobileQRCode />}
      <div className="m-auto" style={{ wordBreak: "break-word" }}>
        <label className="text-center h2">
          {props.additionalInstructions}
        </label>
      </div>
      <Footer
        backButton={reprintButton}
        nextButton={
          <FooterButton
            className={`${isAnonymous ? 'invisible' : ''}`}
            disabled={!stagedOrderComplete}
            onClick={() => props.loadCompleted()}>
            <Trans>Done</Trans>
          </FooterButton>
        } />
    </ContainerTextHeader>
  </PopUpMessageView>
}

export default TicketInstructions;
